import { Button, CircularProgress, Typography } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { VisuallyHiddenInput } from "components/UiComponents";
import { useState, useRef } from "react";
import { useSnackbar } from "notistack";
import { useAuth } from "contexts/AuthContext";
import { managedFetch } from "utils/FetchManager";
import { API_URL } from "config";

// Max custom LCP size. LCP GeoTIFFs are uploaded straight to GCS via a signed
// URL (so they bypass Cloud Run's 32 MiB request limit), but we still cap the
// size to keep server-side validation well within instance memory.
const MAX_LCP_BYTES = 300 * 1024 * 1024;

async function readDetail(response) {
  try {
    return (await response.json())?.detail;
  } catch {
    return "";
  }
}

// Uploads a custom LCP GeoTIFF directly to GCS via a backend-issued signed URL,
// then asks the backend to validate it (8 bands, CRS) and return its bounds.
// The resulting gs:// path + metadata are handed back via onUploaded so the
// form field and bounds box can be populated.
export default function CustomLcpUploadButton({ onUploaded }) {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const onFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".tif")) {
      enqueueSnackbar("Landscape file must be a .tif", { variant: "error" });
      return;
    }
    if (file.size > MAX_LCP_BYTES) {
      enqueueSnackbar("Landscape file must be 300 MB or smaller", {
        variant: "error",
      });
      return;
    }

    const token = currentUser?.accessToken;
    if (!token) {
      enqueueSnackbar("No authentication token available", {
        variant: "error",
      });
      return;
    }

    const jsonHeaders = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    setIsUploading(true);
    try {
      // 1. Ask the backend for a short-lived signed PUT URL + the gs:// path.
      const urlResp = await managedFetch(
        `${API_URL}/custom-lcp/upload-url`,
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ filename: file.name }),
        },
        currentUser
      );
      if (!urlResp || urlResp.status >= 300) {
        throw new Error(
          (await readDetail(urlResp)) || "Could not start upload"
        );
      }
      const { upload_url, custom_lcp_file, content_type } =
        await urlResp.json();

      // 2. Upload the file straight to GCS. The Content-Type must match what
      //    the URL was signed with, and no auth header is sent (the signed URL
      //    carries the authorization).
      const putResp = await window.fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": content_type },
        body: file,
      });
      if (!putResp.ok) {
        throw new Error(`Upload to storage failed (${putResp.status})`);
      }

      // 3. Validate the uploaded raster and fetch its bounds/CRS.
      const valResp = await managedFetch(
        `${API_URL}/custom-lcp/validate`,
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ custom_lcp_file }),
        },
        currentUser
      );
      if (!valResp || valResp.status >= 300) {
        throw new Error((await readDetail(valResp)) || "Validation failed");
      }

      const data = await valResp.json();
      // data: { custom_lcp_file, bounds, crs, band_count }
      onUploaded(data);
      enqueueSnackbar(
        `Landscape file uploaded${data.crs ? ` (EPSG:${data.crs})` : ""}`,
        { variant: "success" }
      );
    } catch (err) {
      console.error("Failed to upload landscape file:", err);
      enqueueSnackbar(`Failed to upload landscape file: ${err.message}`, {
        variant: "error",
      });
    } finally {
      setIsUploading(false);
      // Clear the input so the same file can be re-selected.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Button
      variant="text"
      color="primary"
      component="label"
      disabled={isUploading}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        flex: "0 0 110px",
      }}
    >
      {isUploading ? (
        <CircularProgress size={20} />
      ) : (
        <FileUploadOutlinedIcon />
      )}
      <VisuallyHiddenInput
        ref={fileInputRef}
        type="file"
        accept=".tif"
        onChange={onFileChange}
      />
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ textTransform: "none" }}
      >
        Upload Landscape
      </Typography>
    </Button>
  );
}
